import { useMutation, useQueryClient } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { TFunction, useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";
import * as yup from "yup";

import { NotificationToast } from "../components/Generic/Toast/NotificationToast";
import { PRIVATE_ROUTES } from "../constants";
import { useAppState } from "../context/StateContext";
import { Access } from "../models/access";
import { AutomaticSequencing } from "../models/automatic.sequencing";
import { Error } from "../models/error";
import { Sales as SalesQuote } from "../models/sales";
import JobEstimateCustomerService from "../services/job.estimate.customer.service";
import QuoteService from "../services/quote.service";
import { haveChangesAccess } from "../util/actions";
import useActionAutomaticSequence from "./useActionAutomaticSequence";
import { useQuoteContext } from "./useQuoteContext";
import { useTimezoneForJob } from "./useResolveTimezone";
import { getBrowserTimezoneName } from "Util/getBrowserTimezone";
import { timezone } from "../translate/locales/en_US";

interface SaveQuoteProps {
  id?: number;
  quote: SalesQuote;
}

export const useActionQuote = () => {
  const { quote, setQuote, changedOptions, setGeneralErrors } = useQuoteContext();
  const { currentUser, hasUnsavedChanges, setHasUnsavedChanges } = useAppState();
  const { isAutomaticSequenceReference, increaseAutomaticSequence } = useActionAutomaticSequence();

  const { getTimezone } = useTimezoneForJob();

  const queryClient = useQueryClient();

  const { t: translate } = useTranslation();

  const history = useHistory();

  const schema = yup.object().shape({
    pickupAccess: yup
      .object()
      .nullable()
      .test("pickupAccess", (access) => {
        const accessChanged = haveChangesAccess(quote?.pickupAccess);

        if (accessChanged && access && !access.accessDetailsDescribed) return false;

        return true;
      }),
    deliveryAccess: yup
      .object()
      .nullable()
      .test("deliveryAccess", (access) => {
        const accessChanged = haveChangesAccess(quote?.deliveryAccess);

        if (accessChanged && access && !access.accessDetailsDescribed) return false;

        return true;
      }),
  });

  const onDeleteRoom = (roomName: string) => {
    const inventory = quote?.inventory || [];
    const inventoryDeleted = quote?.inventoryDeleted || [];

    if (inventory) {
      inventory?.forEach((roomToUpdate, index: number) => {
        if (roomToUpdate.name === roomName) {
          const roomPicturesToDelete = [
            ...(roomToUpdate?.roomPictures || []),
            ...(roomToUpdate?.roomPicturesDeleted || []),
          ];
          const itemsJobToDelete = [
            ...(roomToUpdate?.itemsJob || []),
            ...(roomToUpdate?.itemsJobDeleted || []),
          ];
          const materialsToDelete = [
            ...(roomToUpdate?.materials || []),
            ...(roomToUpdate?.materialsDeleted || []),
          ];

          if (roomToUpdate.id) {
            inventoryDeleted.push({
              ...roomToUpdate,
              itemsJobDeleted: itemsJobToDelete,
              roomPicturesDeleted: roomPicturesToDelete,
              materialsDeleted: materialsToDelete,
            });
          }
          inventory.splice(index, 1);
        }
      });
      setQuote((prev) => ({
        ...prev,
        inventory,
        inventoryDeleted,
      }));
    }
  };

  const validateQuote = (translate: TFunction<"translation", undefined>, quote: SalesQuote) => {
    if (!quote.jobTypeId) {
      NotificationToast.warning(translate("pleaseSelectAQuoteType"));

      return false;
    }
    if (!quote.reference) {
      NotificationToast.warning(translate("pleaseFillAReference"));

      return false;
    }
    if (!quote.jobSubStatus) {
      NotificationToast.warning(translate("pleaseSelectAStatus"));

      return false;
    }
    if (!quote.user || !quote.user.id) {
      NotificationToast.warning(translate("pleaseSelectAConsultant"));

      return false;
    }
    if (!quote.mode) {
      NotificationToast.warning(translate("pleaseSelectAMode"));

      return false;
    }
    if (!quote.origin?.customer || !quote.origin?.customer.name || !quote.origin.customer.email) {
      NotificationToast.warning(translate("pleaseFillPickUpCustomerFields"));

      return false;
    }
    if (!quote.origin.locations || quote.origin.locations.length <= 0) {
      NotificationToast.warning(translate("pleaseFillPickUpAddressFields"));

      return false;
    }
    if (
      !quote.destination?.customer ||
      !quote.destination?.customer.name ||
      !quote.destination.customer.email
    ) {
      NotificationToast.warning(translate("pleaseFillDeliveryCustomerFields"));

      return false;
    }
    if (!quote.destination.locations || quote.destination.locations.length <= 0) {
      NotificationToast.warning(translate("pleaseFillDeliveryAddressFields"));

      return false;
    }

    for (const location of quote.origin.locations) {
      if (location?.id && !String(location.id).includes("delete") && !location.longAddress) {
        NotificationToast.warning(translate("pleaseFillPickUpAddressFields"));

        return false;
      }
    }

    for (const location of quote.destination.locations) {
      if (location?.id && !String(location.id).includes("delete") && !location.longAddress) {
        NotificationToast.warning(translate("pleaseFillDeliveryAddressFields"));

        return false;
      }
    }

    return true;
  };

  const handleAccessValidation = (
    translate: TFunction<"translation", undefined>,
    accessPickup?: Access,
    accessDelivery?: Access
  ) => {
    try {
      schema.validateSync(
        {
          pickupAccess: accessPickup,
          deliveryAccess: accessDelivery,
        },
        {
          abortEarly: false,
        }
      );
      setGeneralErrors([]);

      return true;
    } catch (error) {
      if (error instanceof yup.ValidationError) setGeneralErrors(error.inner);
      NotificationToast.warning(translate("pleaseCheckTheAccessDetailsDescribedOnPickupAccess"));

      return false;
    }
  };

  const sendQuote = async (
    callback?: (quoteId?: number) => void,
    sequence?: AutomaticSequencing
  ) => {
    if (
      quote &&
      validateQuote(translate, quote) &&
      handleAccessValidation(translate, quote?.pickupAccess, quote?.deliveryAccess)
    ) {
      setHasUnsavedChanges(false);
      mutate(
        { id: quote?.id, quote },
        {
          onSuccess: async ({ data }) => {
            NotificationToast.success(translate("operationPerformedSuccessfully"));

            await queryClient.invalidateQueries({ queryKey: ["quotes.list"] });
            await queryClient.invalidateQueries({ queryKey: ["quotes.count"] });

            if (
              sequence &&
              quote.reference &&
              sequence.id &&
              isAutomaticSequenceReference(sequence, quote.reference) &&
              !quote?.id
            ) {
              increaseAutomaticSequence({
                sequence,
                value: quote.reference,
              });
            }
            if (callback && data) {
              callback(data.id);
            } else {
              history.push(PRIVATE_ROUTES.QUOTES);
            }
          },
        }
      );
    }
  };

  const { mutate: sendToCustomerMutate, isLoading: sendToCustomerLoading } = useMutation(
    ["send.to.customer"],
    ({ id }: { id: number }) => JobEstimateCustomerService.sendLinkToCustomer(id),
    {
      onError: (err: AxiosError<Error>) => {
        NotificationToast.error(err.message);
      },
    }
  );

  const sendToCustomer = (jobId: number, callback?: () => void) =>
    sendToCustomerMutate(
      { id: jobId },
      {
        onSuccess: () => {
          NotificationToast.success(translate("theEstimatedQuoteWasSuccessfullyShared"));
          if (callback) callback();
        },
      }
    );

  const sendQuoteToCustomer = async (
    callback?: (quoteId: number) => void,
    sequence?: AutomaticSequencing,
    reloadPage?: () => void
  ) => {
    if (
      quote &&
      validateQuote(translate, quote) &&
      handleAccessValidation(translate, quote?.pickupAccess, quote?.deliveryAccess)
    ) {
      setHasUnsavedChanges(false);
      mutate(
        { id: quote?.id, quote },
        {
          onSuccess: (response) => {
            NotificationToast.success(translate("operationPerformedSuccessfully"));
            sendToCustomer(Number(quote?.id ?? response.data.id), reloadPage);
            if (
              sequence &&
              quote.reference &&
              sequence.id &&
              isAutomaticSequenceReference(sequence, quote.reference) &&
              !quote?.id
            )
              increaseAutomaticSequence({
                sequence,
                value: quote.reference,
              });
            if (callback) {
              callback(response.data.id);
            }
          },
        }
      );
    }
  };

  const {
    mutate,
    isLoading: loadingUpdateQuote,
    status: statusUpdateQuote,
  } = useMutation(["quote.save"], ({ id, quote }: SaveQuoteProps) => {
    const enhancedQuote = {
      ...quote,
      isSalesApp: true,
      companyId: currentUser?.companyId,
      timezone: getTimezone(),
    };

    console.log("[useActionQuote] Saving quote with timezone:", {
      resolvedTimezone: enhancedQuote.timezone,
      user: currentUser?.timezone?.name,
      browser: getBrowserTimezoneName(),
    });

    if (id) {
      return QuoteService.update(Number(quote.id), enhancedQuote, {
        actions: changedOptions,
      });
    }

    return QuoteService.create(enhancedQuote);
  });

  return {
    onDeleteRoom,
    sendQuote,
    sendToCustomer,
    loadingUpdateQuote,
    statusUpdateQuote,
    hasUnsavedChanges,
    setHasUnsavedChanges,
    sendQuoteToCustomer,
    sendToCustomerLoading,
  };
};
